'use client';

import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';

import { cn } from '@ui/lib/utils';

const Tabs = forwardRef<HTMLDivElement, TabsPrimitive.Root.Props>(
  ({ className, orientation = 'horizontal', ...props }, ref) => (
    <TabsPrimitive.Root
      ref={ref}
      data-slot="tabs"
      data-orientation={orientation}
      className={cn('group/tabs flex gap-2 data-[orientation=horizontal]:flex-col', className)}
      {...props}
    />
  )
);
Tabs.displayName = 'Tabs';

enum TabsVariant {
  DEFAULT = 'default',
  LINE = 'line',
  PILLS = 'pills',
  SEGMENTED = 'segmented',
}

const tabsListVariants = cva(
  cn(
    'group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground',
    'group-data-[orientation=horizontal]/tabs:h-8 group-data-[orientation=vertical]/tabs:h-fit',
    'group-data-[orientation=vertical]/tabs:flex-col',
    'data-[variant=line]:rounded-none'
  ),
  {
    variants: {
      variant: {
        [TabsVariant.DEFAULT]: 'bg-muted',
        [TabsVariant.LINE]: 'gap-1 bg-transparent',
        [TabsVariant.PILLS]: cn(
          'gap-1 rounded-full bg-surface p-1 shadow-elevation-1',
          'group-data-[orientation=horizontal]/tabs:h-[42px]'
        ),
        [TabsVariant.SEGMENTED]: cn(
          'gap-0.5 rounded-full bg-surface-2 p-0.75',
          'group-data-[orientation=horizontal]/tabs:h-9'
        ),
      },
      size: {
        default: '',
        sm: 'p-[2px] group-data-[orientation=horizontal]/tabs:h-7',
      },
    },
    defaultVariants: {
      variant: TabsVariant.DEFAULT,
      size: 'default',
    },
  }
);

type TabsListProps = TabsPrimitive.List.Props &
  Omit<VariantProps<typeof tabsListVariants>, 'variant'> & { variant?: TabsVariant };

const TabsList = forwardRef<HTMLDivElement, TabsListProps>(
  ({ className, variant = TabsVariant.DEFAULT, size = 'default', ...props }, ref) => (
    <TabsPrimitive.List
      ref={ref}
      data-slot="tabs-list"
      data-variant={variant}
      data-size={size}
      className={cn(tabsListVariants({ variant, size }), className)}
      {...props}
    />
  )
);
TabsList.displayName = 'TabsList';

const TabsTrigger = ({ className, ...props }: TabsPrimitive.Tab.Props) => (
  <TabsPrimitive.Tab
    data-slot="tabs-trigger"
    className={cn(
      'relative inline-flex h-[calc(100%-1px)] flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md',
      'border border-transparent px-1.5 py-0.5 text-sm font-medium whitespace-nowrap text-foreground/60',
      'transition-all group-data-[orientation=vertical]/tabs:w-full',
      'group-data-[orientation=vertical]/tabs:justify-start hover:text-foreground',
      'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
      'focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50',
      'has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1',
      'aria-disabled:pointer-events-none aria-disabled:opacity-50',
      'dark:text-muted-foreground dark:hover:text-foreground',
      'group-data-[variant=default]/tabs-list:data-active:shadow-sm',
      'group-data-[variant=line]/tabs-list:data-active:shadow-none',
      "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      'group-data-[variant=line]/tabs-list:bg-transparent',
      'group-data-[variant=line]/tabs-list:data-active:bg-transparent',
      'dark:group-data-[variant=line]/tabs-list:data-active:border-transparent',
      'dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent',
      'data-active:bg-background data-active:text-foreground dark:data-active:border-input',
      'dark:data-active:bg-input/30 dark:data-active:text-foreground',
      'after:absolute after:bg-foreground after:opacity-0 after:transition-opacity',
      'group-data-[orientation=horizontal]/tabs:after:inset-x-0',
      'group-data-[orientation=horizontal]/tabs:after:bottom-[-5px]',
      'group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0',
      'group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5',
      'group-data-[variant=line]/tabs-list:data-active:after:opacity-100',
      'group-data-[variant=line]/tabs-list:data-active:text-primary',
      'group-data-[variant=line]/tabs-list:data-active:font-semibold',
      'group-data-[variant=line]/tabs-list:after:bg-primary',
      'group-data-[variant=pills]/tabs-list:h-[34px] group-data-[variant=pills]/tabs-list:flex-none',
      'group-data-[variant=pills]/tabs-list:rounded-full group-data-[variant=pills]/tabs-list:px-3.5',
      'group-data-[variant=pills]/tabs-list:gap-[7px] group-data-[variant=pills]/tabs-list:text-[12.5px]',
      'group-data-[variant=pills]/tabs-list:font-bold group-data-[variant=pills]/tabs-list:text-fg-muted',
      'group-data-[variant=pills]/tabs-list:hover:text-fg-strong',
      'group-data-[variant=pills]/tabs-list:data-active:bg-accent-soft',
      'group-data-[variant=pills]/tabs-list:data-active:text-primary',
      'group-data-[variant=pills]/tabs-list:data-active:shadow-none',
      'group-data-[variant=pills]/tabs-list:after:hidden',
      'group-data-[variant=pills]/tabs-list:focus-visible:border-transparent',
      'group-data-[variant=pills]/tabs-list:focus-visible:ring-2',
      'group-data-[variant=pills]/tabs-list:focus-visible:ring-primary/30',
      "group-data-[variant=pills]/tabs-list:[&_svg:not([class*='size-'])]:size-[15px]",
      'group-data-[variant=segmented]/tabs-list:flex-none group-data-[variant=segmented]/tabs-list:rounded-full',
      'group-data-[variant=segmented]/tabs-list:px-3.5 group-data-[variant=segmented]/tabs-list:gap-1.5',
      'group-data-[variant=segmented]/tabs-list:text-[12.5px] group-data-[variant=segmented]/tabs-list:font-bold',
      'group-data-[variant=segmented]/tabs-list:text-fg-muted',
      'group-data-[variant=segmented]/tabs-list:hover:text-fg-strong',
      'group-data-[variant=segmented]/tabs-list:data-active:bg-surface',
      'group-data-[variant=segmented]/tabs-list:data-active:text-fg-strong',
      'group-data-[variant=segmented]/tabs-list:data-active:shadow-elevation-1',
      'group-data-[variant=segmented]/tabs-list:after:hidden',
      'group-data-[variant=segmented]/tabs-list:focus-visible:border-transparent',
      'group-data-[size=sm]/tabs-list:px-2.5 group-data-[size=sm]/tabs-list:text-[11px]',
      'group-data-[variant=segmented]/tabs-list:focus-visible:ring-2',
      'group-data-[variant=segmented]/tabs-list:focus-visible:ring-primary/30',
      className
    )}
    {...props}
  />
);

const TabsContent = ({ className, ...props }: TabsPrimitive.Panel.Props) => (
  <TabsPrimitive.Panel data-slot="tabs-content" className={cn('flex-1 text-sm outline-none', className)} {...props} />
);

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants, TabsVariant };
